import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_BackgroundChanger = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/background-changer',
			name: 'Background Change — AI Photo Background Changer | Lift',
			description:
				'Change photo background instantly with AI. No watermark. Upload photo, replace backgrounds in seconds, and create professional images with any backdrop. Try now!',
			url: 'https://liftapp.ai/background-changer',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/background-changer#product' },
				{ '@id': 'https://liftapp.ai/background-changer#howto' },
				{ '@id': 'https://liftapp.ai/background-changer#benefits' },
				{ '@id': 'https://liftapp.ai/background-changer#faq' },
				{ '@id': 'https://liftapp.ai/background-changer#why-lift' },
				{ '@id': 'https://liftapp.ai/background-changer#explore-tools' },
				{ '@id': 'https://liftapp.ai/background-changer#all-in-one' },
			],
		},
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/background-changer#product',
			name: 'AI Background Changer',
			description:
				'Change photo background instantly with AI. No watermark. Upload photo, replace backgrounds in seconds, and create professional images with any backdrop.',
			url: 'https://liftapp.ai/background-changer',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/background-changer#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift changes your image background automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/background-changer#benefits',
			name: 'Who can benefit from AI Background Changer?',
			description:
				"Lift's AI Background Changer is useful for content creators, marketers, small business owners, freelancers, and educators who want professional photo backgrounds.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Create stunning visuals for Instagram, TikTok, and YouTube instantly.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Marketers & SMM managers — Generate multiple versions of ads with different backgrounds for fast A/B testing.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Small business owners — Create professional product photos with clean, consistent backgrounds.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Freelancers — Deliver polished visuals for client presentations and portfolios.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & educators — Enhance presentations and projects with custom backgrounds in seconds.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI background changer',
				description:
					'Before-and-after example of AI background changer by Lift.',
			},
		},
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/background-changer#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Background Changer?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Background Changer is a premium online tool that instantly replaces photo backgrounds with any scene or backdrop with pixel-perfect precision — ideal for product photos, social content, or professional presentations.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Background Changer work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload your photo to Lift's background changer tool. Our AI automatically detects the subject and replaces the background with your chosen image in seconds — no photo editing experience needed. Download up to 20 images with seamless background replacement.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift AI Background Changer uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking results without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/background-changer#why-lift',
			name: "Why Lift's AI Background Changer stands out",
			description:
				"Lift's Background Changer combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Change background of photo',
				description:
					'Before-and-after example of changing background of photo with Lift.',
			},
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/background-changer#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/background-changer#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Background Changer',
					item: 'https://liftapp.ai/background-changer',
				},
			],
		},
	],
};
